import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { apiRequest } from "@/lib/queryClient";
import { HelpCircle, Send, X, Loader2, Sparkles, ThumbsUp, ThumbsDown, Lock } from "lucide-react";

type ChatRole = "user" | "assistant";
interface ChatMessage {
  role: ChatRole;
  content: string;
  messageId?: string;
  feedback?: "up" | "down" | null;
}

interface HelpChatWidgetProps {
  open: boolean;
  onClose: () => void;
}

const WELCOME_MESSAGE: ChatMessage = {
  role: "assistant",
  content:
    "Hi! I'm the Clarik help assistant. Ask me about leads, clients, invoices, scheduling, the email hub, billing, or settings — I'll point you in the right direction. If I can't help, I'll send you to the feedback form.",
};

// Match either a /feedback link or a /help-kb/<slug> link. Both are
// rewritten to local wouter Links so navigation stays inside the SPA.
const APP_LINK_REGEX = /(?:https?:\/\/[^\s/]+)?\/(?:feedback|help-kb\/[a-z0-9-]+)(?:[/?#][^\s)\]]*)?/gi;

function renderAssistantContent(text: string): JSX.Element {
  const parts: Array<JSX.Element | string> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = APP_LINK_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const matched = match[0];
    const localPath = matched.replace(/^https?:\/\/[^/]+/i, "");
    const isKb = localPath.startsWith("/help-kb/");
    parts.push(
      <Link
        key={`lnk-${key++}`}
        href={localPath}
        className="underline text-primary hover:opacity-80"
        data-testid={isKb ? "link-help-chat-kb" : "link-help-chat-feedback"}
      >
        {matched}
      </Link>,
    );
    lastIndex = match.index + matched.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  if (parts.length === 0) parts.push(text);
  return <>{parts}</>;
}

function HelpChatWidgetInner({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [unavailable, setUnavailable] = useState<{ shown: boolean; reason?: string }>({ shown: false });
  const [chatId, setChatId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/help-chat/availability", { credentials: "include" });
        if (cancelled || !res.ok) return;
        const json = (await res.json()) as { available?: boolean; reason?: string };
        if (!cancelled && json && json.available === false) {
          setUnavailable({ shown: true, reason: json.reason });
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, escalating]);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setSending(true);
    try {
      const res = await apiRequest("POST", "/api/help-chat", { chatId: chatId || undefined, message: trimmed });
      const json = (await res.json()) as {
        chatId?: string;
        messageId?: string;
        reply?: string;
        unavailable?: boolean;
        reason?: string;
      };
      if (json.unavailable) {
        setUnavailable({ shown: true, reason: json.reason });
      } else if (json.reply) {
        if (json.chatId) setChatId(json.chatId);
        setMessages((prev) => [...prev, { role: "assistant", content: json.reply!, messageId: json.messageId }]);
      }
    } catch (err: any) {
      const m = (err?.message || "").match(/^\d+:\s*(.+)$/);
      if (m) {
        try {
          const parsed = JSON.parse(m[1]);
          if (parsed?.unavailable) { setUnavailable({ shown: true, reason: parsed?.reason }); return; }
        } catch { /* ignore */ }
      }
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong reaching the help service. Please try again in a moment, or share what you need on /feedback." },
      ]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const recordFeedback = async (messageId: string, rating: "up" | "down") => {
    if (!chatId) return;
    // Optimistic update.
    setMessages((prev) => prev.map((m) => (m.messageId === messageId ? { ...m, feedback: rating } : m)));
    try {
      await apiRequest("POST", "/api/help-chat/feedback", { chatId, messageId, rating });
    } catch { /* ignore */ }
    if (rating === "down") {
      setEscalating(true);
      try {
        const res = await apiRequest("POST", "/api/help-chat/escalate", { chatId });
        const json = (await res.json()) as { reply?: string; messageId?: string; unavailable?: boolean; reason?: string };
        if (json.unavailable) {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: "I tried a deeper look but escalation is unavailable right now. Please share what you need on /feedback." },
          ]);
        } else if (json.reply) {
          setMessages((prev) => [...prev, { role: "assistant", content: json.reply!, messageId: json.messageId }]);
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Escalation failed. Please share what you need on /feedback." },
        ]);
      } finally {
        setEscalating(false);
      }
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
  };

  const upgradeRequired = unavailable.shown && unavailable.reason === "upgrade_required";
  // Per spec, spend caps are notify-only — the assistant keeps serving
  // requests past the cap. We intentionally do NOT branch into a
  // hard-stop "capped" UI even if the server ever surfaces such a reason.
  const disabled = unavailable.shown && (unavailable.reason === "disabled_global" || unavailable.reason === "disabled_workspace");

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-[min(380px,calc(100vw-2rem))] h-[min(540px,calc(100vh-2rem))] rounded-lg border bg-background shadow-2xl flex flex-col"
      role="dialog" aria-label="Help chat" data-testid="panel-help-chat"
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" aria-hidden="true" />
          <span className="text-sm font-semibold" data-testid="text-help-chat-title">Help</span>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close help" data-testid="button-help-chat-close">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="list-help-chat-messages">
        {upgradeRequired ? (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3 px-4" data-testid="state-help-chat-upgrade">
            <Lock className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm font-medium">Help assistant is a paid feature</p>
            <p className="text-xs text-muted-foreground">
              Upgrade to Pro, Business, or Enterprise to chat with the in-app help assistant.
            </p>
            <Link href="/upgrade" className="text-sm underline text-primary" data-testid="link-upgrade">
              See plans
            </Link>
          </div>
        ) : disabled ? (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3 px-4" data-testid="state-help-chat-disabled">
            <HelpCircle className="w-8 h-8 text-muted-foreground" />
            <p className="text-sm font-medium">Help is unavailable right now</p>
            <p className="text-xs text-muted-foreground">
              The assistant is paused. Share what you need on{" "}
              <Link href="/feedback" className="underline text-primary">the feedback page</Link>{" "}and the team will follow up.
            </p>
          </div>
        ) : unavailable.shown ? (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3 px-4" data-testid="state-help-chat-unavailable">
            <HelpCircle className="w-8 h-8 text-muted-foreground" />
            <p className="text-sm font-medium">Help is unavailable right now</p>
            <p className="text-xs text-muted-foreground">
              The help assistant isn't configured at the moment. In the meantime, you can describe what you need on the{" "}
              <Link href="/feedback" className="underline text-primary" data-testid="link-help-chat-unavailable-feedback">feedback page</Link>{" "}and the team will follow up.
            </p>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"} data-testid={`message-help-chat-${m.role}-${i}`}>
              <div className="max-w-[85%] flex flex-col gap-1">
                <div className={"rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words " + (m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground")}>
                  {m.role === "assistant" ? renderAssistantContent(m.content) : m.content}
                </div>
                {m.role === "assistant" && m.messageId && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => recordFeedback(m.messageId!, "up")}
                      className={"p-1 rounded hover:bg-muted " + (m.feedback === "up" ? "text-primary" : "text-muted-foreground")}
                      aria-label="Helpful"
                      data-testid={`button-feedback-up-${m.messageId}`}
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => recordFeedback(m.messageId!, "down")}
                      className={"p-1 rounded hover:bg-muted " + (m.feedback === "down" ? "text-destructive" : "text-muted-foreground")}
                      aria-label="Not helpful"
                      data-testid={`button-feedback-down-${m.messageId}`}
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {sending && !unavailable.shown && (
          <div className="flex justify-start" data-testid="indicator-help-chat-loading">
            <div className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
            </div>
          </div>
        )}
        {escalating && (
          <div className="flex justify-start" data-testid="indicator-help-chat-escalating">
            <div className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking harder…
            </div>
          </div>
        )}
      </div>

      {!unavailable.shown && (
        <div className="border-t p-3">
          <div className="flex gap-2 items-end">
            <Textarea
              ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
              placeholder="Ask a question about the app…" rows={2} className="resize-none text-sm"
              disabled={sending || escalating} data-testid="input-help-chat-message"
            />
            <Button type="button" size="icon" onClick={() => void send()} disabled={sending || escalating || !input.trim()} aria-label="Send message" data-testid="button-help-chat-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground" data-testid="text-help-chat-scope-disclaimer">
            I can only help with Clarik.
          </p>
          <p className="mt-1 text-[10px] text-muted-foreground">
            AI-generated answers may be inaccurate. Conversations are retained briefly to improve the assistant.
          </p>
        </div>
      )}
    </div>
  );
}

export function HelpChatWidget({ open, onClose }: HelpChatWidgetProps) {
  if (!open) return null;
  return <HelpChatWidgetInner onClose={onClose} />;
}
